/**
 * WuKong IM 全局连接插件
 *
 * @description 在用户登录后自动连接 WuKong IM，全局监听新消息
 *              复用 useWuKongIM composable 中的连接方法，避免代码重复
 *              支持监听登录状态，用户登录时自动连接
 * @author system
 * @date 2025-01-21
 */

import { watch } from 'vue'
import { useWuKongIMStore } from '~/stores/wukongIM'
import { useGlobalStore } from '~/stores/global'

export default defineNuxtPlugin(async nuxtApp => {
  if (!import.meta.client) return

  const { useWuKongIM } = await import('~/composables/chat/useWuKongIM')
  const { useInboxUnread } = await import('~/composables/chat/useInboxUnread')

  const { refreshInboxUnread } = useInboxUnread()
  const route = useRoute()

  let isLastConsultation = false
  let isConnected = false

  /** 首屏拉取 inbox 未读；若有客服未读则提示（刷新后/重进站点场景） */
  const getLastConsultation = async () => {
    const accessToken = useCookie('access_token')
    if (!accessToken.value) {
      return
    }
    if (isLastConsultation || route.fullPath.includes('/chat/service/')) {
      return
    }
    try {
      await refreshInboxUnread()
      const wukongIMStore = useWuKongIMStore()
      if (wukongIMStore.serviceUnread > 0) {
        ElNotification({
          title: '💬 新消息',
          message: `<div style="cursor: pointer; display: flex; align-items: center; gap: 10px; padding: 4px 0;">
              <div style="width: 36px; height: 36px; border-radius: 50%; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); display: flex; align-items: center; justify-content: center; color: #fff; font-size: 14px; font-weight: 600; flex-shrink: 0;">
                客
              </div>
              <div style="flex: 1; min-width: 0;">
                <div style="font-size: 13px; color: #909399;">给您发来了一条新消息，点击查看</div>
              </div>
              <div style="color: #1890ff; font-size: 20px;">›</div>
            </div>`,
          position: 'bottom-right',
          duration: 4000,
          dangerouslyUseHTMLString: true,
          onClick: () => {
            const { openService: openCustomerService } = useCustomerService()
            openCustomerService()
          },
        })
      }
      isLastConsultation = true
    } catch (error) {
      console.error('获取 inbox 未读数失败:', error)
    }
  }

  const checkAndConnect = async () => {
    console.log('🔌 [WuKongIM Plugin] checkAndConnect 开始执行')

    if (isConnected) {
      console.log('👤 [WuKongIM Plugin] 已连接，跳过重复连接')
      return
    }

    const globalStore = useGlobalStore()
    const accessToken = useCookie('access_token')
    console.log('🔌 [WuKongIM Plugin] access_token:', accessToken.value ? '存在' : '不存在')

    if (!accessToken.value) {
      console.log('👤 [WuKongIM Plugin] 用户未登录，跳过连接')
      return
    }

    let userId = globalStore.user?.id || globalStore.user?.userId
    console.log('🔌 [WuKongIM Plugin] 当前 userId:', userId)

    if (!userId) {
      try {
        console.log('🔌 [WuKongIM Plugin] 正在获取用户信息...')
        await globalStore.getUser()
        userId = globalStore.user?.id || globalStore.user?.userId
        console.log('🔌 [WuKongIM Plugin] 获取用户信息后 userId:', userId)
      } catch (error) {
        console.error('🔌 [WuKongIM Plugin] 获取用户信息失败:', error)
        return
      }
    }

    if (userId && accessToken.value) {
      console.log('🔌 [WuKongIM Plugin] 准备连接 WuKong IM, userId:', userId)
      const { connect } = useWuKongIM()
      try {
        await connect(String(userId), accessToken.value)
        isConnected = true
      } catch (error) {
        console.error('🔌 [WuKongIM Plugin] WuKong IM 连接失败:', error)
      }
    } else {
      console.log('🔌 [WuKongIM Plugin] 缺少 userId 或 token，无法连接')
    }
  }

  const watchLoginStatus = () => {
    const globalStore = useGlobalStore()

    watch(
      () => globalStore.isLogin,
      async (newVal, oldVal) => {
        if (newVal && !oldVal) {
          console.log('👤 检测到用户登录，准备连接 WuKong IM')
          await new Promise(resolve => setTimeout(resolve, 200))
          await checkAndConnect()
          await getLastConsultation()
        } else if (!newVal && oldVal) {
          console.log('👤 检测到用户登出，断开 WuKong IM 连接')
          const { disconnect } = useWuKongIM()
          disconnect()
          isConnected = false
          isLastConsultation = false
          useWuKongIMStore().clearInboxUnread()
        }
      },
      { immediate: false }
    )
  }

  nuxtApp.hook('app:mounted', () => {
    console.log('🔌 [WuKongIM Plugin] app:mounted 钩子触发')
    setTimeout(() => {
      console.log('🔌 [WuKongIM Plugin] 延迟执行开始')
      checkAndConnect()
      getLastConsultation()
      watchLoginStatus()
    }, 1500)
  })

  return {
    provide: {
      wukongIM: {
        connect: async (userId: string, token: string) => {
          const { connect } = useWuKongIM()
          await connect(userId, token)
          isConnected = true
        },
        disconnect: () => {
          const { disconnect } = useWuKongIM()
          disconnect()
          isConnected = false
        },
        checkAndConnect
      }
    }
  }
})
